import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Stack } from '@mui/system';
import strings from 'common/Translation/Translate';
import DrawerMenu from 'components/DrawerMenu';
import Form from 'components/Form';
import Section from 'components/Section/Section';
import { MouseEvent, RefObject, useCallback, useEffect, useRef, useState } from 'react';
import axiosInstance from 'utils/axios';
import { images } from 'utils/constants/images';
import { URLS } from 'utils/constants/urls';
import { createDropdownData, getDrawerWidth, removeErrorFieldsFromValues } from 'utils/validators/HelperFunctions';
import PageLoader from 'components/PageLoader';
import ConfirmationModal from 'components/ConfirmationModal';
import { SalesManagerTargetForm } from './SalesManagerTargetForm';
import { formatCurrencyWithSymbol } from 'utils/hooks/getCurrency';

type SalesManagerTargetProps = {
  setMessage: any;
  openDrawer: any;
  setOpenDrawer: any;
  branchId: number;
};

const SalesManagerTarget = ({ setMessage, openDrawer, setOpenDrawer, branchId }: SalesManagerTargetProps) => {
  const theme = useTheme();
  const screenSizeDownSm = useMediaQuery(theme.breakpoints.down('sm'));
  const screenSizeDownMd = useMediaQuery(theme.breakpoints.down('md'));
  const screenSizeUpLg = useMediaQuery(theme.breakpoints.down('lg'));

  const [groupedTargets, setGroupedTargets] = useState<any>({});
  const [expandedManagers, setExpandedManagers] = useState<Set<string>>(new Set());
  const [hasError, setHasError] = useState<boolean>(false);
  const [selectedRowId, setSelectedRowId] = useState<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [pageResponse, setPageResponse] = useState<any>({});

  // Dropdowns
  const [users, setUsers] = useState<any[]>([]);
  const [periods, setPeriods] = useState<any[]>([]);

  let formRef: RefObject<Form | null | undefined> = useRef();

  const handleClose = () => {
    setOpenDrawer(false);
    setPageResponse({});
    setSelectedRowId(null);
    setHasError(false);
    formRef.current?.resetForm();
  };

  const handleCatch = (error: any) => {
    setMessage({
      display: true,
      severity: 'error',
      message: error.response?.data?.error_description ?? error.response?.data?.message ?? error.message,
    });
  };

  const fetchUsers = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get(URLS.getBranchWiseManagersDropdown(branchId));
      if (data && data.length > 0) {
        setUsers(createDropdownData(data, ['id', 'name']));
      }
    } catch (error: any) {
      handleCatch(error);
    }
  }, [branchId]);

  const fetchPeriods = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get(URLS.getTargetPeriodsDropdown('MONTHLY'));
      if (data && data.length > 0) {
        setPeriods(createDropdownData(data, ['id', 'name']));
      }
    } catch (error: any) {
      handleCatch(error);
    }
  }, []);

  const fetchTargets = useCallback(async () => {
    try {
      setIsLoading(true);
      const body: any = { page: 1, size: 10, sort: ['createdAt:desc'] };
      const { data } = await axiosInstance.post(URLS.salesManagerTargetPage(branchId), body);

      const content = data?.content?.map((item: any) => ({
        ...item,
        id: item.id,
        email: item.user?.label || item.user?.email || '-',
        userId: item.user?.value || item.user?.id,
        branchName: item.branch?.label || '-',
        periodName: item.period?.label || '-',
      }));

      // Group targets by manager (email/userId)
      const grouped: any = {};
      content.forEach((target: any) => {
        const managerKey = target.userId || target.email;
        if (!grouped[managerKey]) {
          grouped[managerKey] = {
            email: target.email,
            userId: target.userId,
            targets: [],
          };
        }
        grouped[managerKey].targets.push(target);
      });
      setGroupedTargets(grouped);

      // Expand all managers by default
      setExpandedManagers(new Set(Object.keys(grouped)));
    } catch (error: any) {
      handleCatch(error);
    } finally {
      setIsLoading(false);
    }
  }, [branchId]);

  useEffect(() => {
    fetchUsers();
    fetchPeriods();
  }, [fetchUsers, fetchPeriods]);

  useEffect(() => {
    fetchTargets();
  }, [fetchTargets]);

  const handleAccordionChange = (managerKey: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedManagers(prev => {
      const newSet = new Set(prev);
      if (isExpanded) {
        newSet.add(managerKey);
      } else {
        newSet.delete(managerKey);
      }
      return newSet;
    });
  };

  const formatCurrency = (value: any) => {
    return value ? formatCurrencyWithSymbol(Number(value)) : '-';
  };

  const parseNullableInteger = (value: any) => {
    return value || value === 0 ? Number.parseInt(value, 10) : null;
  };

  const handleFormChange = (field: string, value: string | boolean, formData: any) => {
    if (field !== 'autoCalculateAchievedTargets') {
      return;
    }

    const cleanFormData = removeErrorFieldsFromValues(formData);
    const nextFormData = {
      ...pageResponse,
      ...cleanFormData,
      [field]: value,
    };

    setPageResponse(nextFormData);
  };

  const targetsTable = () => {
    const managerKeys = Object.keys(groupedTargets);

    if (managerKeys.length === 0) {
      return null;
    }

    return (
      <Box sx={{ width: '100%', marginBottom: '20px' }}>
        {managerKeys.map(managerKey => {
          const managerData = groupedTargets[managerKey];
          const isExpanded = expandedManagers.has(managerKey);

          return (
            <Accordion
              key={managerKey}
              expanded={isExpanded}
              onChange={handleAccordionChange(managerKey)}
              sx={{
                mb: 1,
                '&:before': {
                  display: 'none',
                },
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              }}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  backgroundColor: `${theme.palette.primary.main}15`,
                  '&:hover': {
                    backgroundColor: `${theme.palette.primary.main}25`,
                  },
                }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                    pr: 2,
                  }}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {managerData.email}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {managerData.targets.length} target(s)
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: `${theme.palette.primary.main}08` }}>
                      <TableCell sx={{ fontWeight: 600 }}>Period</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        Event Count Target
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                      Event Revenue Target
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        Room Night Target
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        Room Revenue Target
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        {strings.customermgmt_column_action}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {managerData.targets.map((target: any, index: number) => (
                      <TableRow
                        key={target.id}
                        sx={{
                          '&:hover': {
                            backgroundColor: `${theme.palette.primary.main}05`,
                          },
                          backgroundColor: index % 2 === 0 ? '#FFFFFF' : '#FAFBFC',
                        }}>
                        <TableCell>{target.periodName}</TableCell>
                        <TableCell align="right">{target.miceCountTarget || '-'}</TableCell>
                        <TableCell align="right">{formatCurrency(target.miceRevenueTarget)}</TableCell>
                        <TableCell align="right">{target.roomNightTarget || '-'}</TableCell>
                        <TableCell align="right">{formatCurrency(target.roomRevenueTarget)}</TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <IconButton
                              size="small"
                              aria-label="Edit"
                              onClick={(e: any) => {
                                e.stopPropagation();
                                editRow(target.id);
                              }}>
                              <img src={images.editIcon} alt="" width="20" height="20" />
                            </IconButton>
                            <IconButton
                              size="small"
                              aria-label="Delete"
                              onClick={(e: any) => {
                                e.stopPropagation();
                                setSelectedRowId(target.id);
                                setShowDeleteModal(true);
                              }}>
                              <img src={images.deleteIcon} alt="" width="20" height="20" />
                            </IconButton>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>
    );
  };

  const handleSave = async (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    const form = formRef.current?.getFormData();
    const isFormValid = form?.isFormValid;
    if (!isFormValid) {
      setHasError(true);
      return;
    }

    let formData = removeErrorFieldsFromValues(form?.formData);

    const payload = {
      user: formData.user,
      branch: branchId,
      period: formData.period,
      autoCalculateAchievedTargets: formData.autoCalculateAchievedTargets === true,
      roomNightTarget: parseNullableInteger(formData.roomNightTarget),
      roomRevenueTarget: parseNullableInteger(formData.roomRevenueTarget),
      miceCountTarget: parseNullableInteger(formData.miceCountTarget),
      miceRevenueTarget: parseNullableInteger(formData.miceRevenueTarget),
      roomNightAchieved: parseNullableInteger(formData.roomNightAchieved),
      roomRevenueAchieved: parseNullableInteger(formData.roomRevenueAchieved),
      miceCountAchieved: parseNullableInteger(formData.miceCountAchieved),
      miceRevenueAchieved: parseNullableInteger(formData.miceRevenueAchieved),
    };

    try {
      setIsLoading(true);
      if (selectedRowId) {
        // Update existing target
        const { status } = await axiosInstance.put(URLS.getSalesManagerTargetById(selectedRowId), payload);
        if (status === 200 || status === 201) {
          setMessage({
            display: true,
            severity: 'success',
            message: strings.common_update_message,
          });
          setSelectedRowId(null);
          setOpenDrawer(false);
          setPageResponse({});
          setHasError(false);
          fetchTargets();
        }
      } else {
        // Create new target
        const { status } = await axiosInstance.post(URLS.addSalesManagerTarget, payload);
        if (status === 200 || status === 201) {
          setMessage({
            display: true,
            severity: 'success',
            message: strings.common_save_message,
          });
          setOpenDrawer(false);
          setPageResponse({});
          setHasError(false);
          fetchTargets();
        }
      }
    } catch (error: any) {
      handleCatch(error);
    } finally {
      setIsLoading(false);
    }
  };

  const editRow = async (id: any) => {
    try {
      setIsLoading(true);
      const { data } = await axiosInstance.get(URLS.getSalesManagerTargetById(id));
      const formatted = {
        ...data,
        user: data.user?.value,
        period: data.period?.value,
        roomNightTarget: data.roomNightTarget || '',
        roomRevenueTarget: data.roomRevenueTarget || '',
        miceCountTarget: data.miceCountTarget || '',
        miceRevenueTarget: data.miceRevenueTarget || '',
        roomNightAchieved: data.roomNightAchieved || '',
        roomRevenueAchieved: data.roomRevenueAchieved || '',
        miceCountAchieved: data.miceCountAchieved || '',
        miceRevenueAchieved: data.miceRevenueAchieved || '',
        autoCalculateAchievedTargets: data.autoCalculateAchievedTargets || false,
      };
      setPageResponse(formatted);
      setSelectedRowId(id);
      setOpenDrawer(true);
    } catch (error: any) {
      handleCatch(error);
    } finally {
      setIsLoading(false);
    }
  };

  const deleteRow = async (id: any) => {
    try {
      setIsLoading(true);
      const { status } = await axiosInstance.delete(URLS.getSalesManagerTargetById(id));
      if (status === 200 || status === 204) {
        setMessage({
          display: true,
          severity: 'success',
          message: strings.common_delete_message,
        });
        setShowDeleteModal(false);
        setSelectedRowId(null);
        fetchTargets();
      }
    } catch (error: any) {
      handleCatch(error);
    } finally {
      setIsLoading(false);
    }
  };

  const formComponent = () => {
    return (
      <>
        {isLoading && <PageLoader />}
        <Grid container spacing={2} sx={{ my: 2 }}>
          <Form
            hasError={hasError}
            ref={formRef as RefObject<Form>}
            model={SalesManagerTargetForm(strings, users, periods, pageResponse.autoCalculateAchievedTargets === true)}
            values={pageResponse}
            onChange={handleFormChange}
          />
        </Grid>

        <Grid item sx={{ my: 1 }} xs={12} sm={3} lg={2}>
          <Button variant="contained" sx={{ color: '#ffffff' }} onClick={e => handleSave(e)} fullWidth>
            {strings.common_button_save}
          </Button>
        </Grid>
      </>
    );
  };

  return (
    <>
      <Section />
      <Box mb={0}>
        <Button variant="text" onClick={() => setOpenDrawer(true)}>
          <span style={{ marginRight: '10px' }}>Sales Manager Target</span>
          <AddCircleOutlineIcon />
        </Button>
        {targetsTable()}
        <ConfirmationModal
          open={showDeleteModal}
          message={strings.confirmDeletedText}
          onCancel={() => {
            setShowDeleteModal(false);
            setSelectedRowId(null);
          }}
          onConfirm={() => {
            deleteRow(selectedRowId);
          }}
        />
        <DrawerMenu
          width={getDrawerWidth(screenSizeDownSm, screenSizeDownMd, screenSizeUpLg)}
          open={openDrawer}
          handleClose={handleClose}
          closeButton={true}
          title="Sales Manager Target">
          {formComponent()}
        </DrawerMenu>
      </Box>
    </>
  );
};
export default SalesManagerTarget;
